import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Box, Button, SpaceBetween, Container, Spinner } from '@amzn/awsui-components-react';
import {
    DataSourceSelectionTable,
    SourceLineSelectionTable,
    WorksheetSelectionTable,
    RecordMode,
} from './MDAWorksheetSteps';
import { DataSourcesContext } from '../data-sources/DataSourcesContainer';
import { CalculationBuilderContext } from '../CalculationBuilderView';
import DataKeySummaryView from '../data-sources/DataKeySummaryView';
import KeyValuePair from 'src/models/common/KeyValuePair';
import { ACTIONS } from 'src/services/calculation-builder/DataSourcesReducer';
import { ACTIONS as CALC_BUILDER_ACTIONS } from 'src/services/calculation-builder/CalculationBuilderReducer';
import StringUtils from 'src/utils/stringUtils';
import { DataSourceRecord } from 'src/models/common/DataSourceRecord';

type WorksheetValueIdentifier = {
    xcellId: string;
    companyCode: string;
    function: string;
    value: number | string;
};

export interface MDAWorksheetSelectorFormState {
    worksheetId: string;
    worksheetVersion: number;
    worksheetName: string;
    worksheetDatasourceId: string;
    worksheetDatasourceDescription: string;
    worksheetDatasourceS3PresignUrl: string;
    worksheetExecutionId: string;
    selectedSourceLineIdentifiers: WorksheetValueIdentifier[];
}

export interface MDAWorksheetSelectorProps {
    initialSelectionData?: MDAWorksheetSelectorFormState;
    onComplete: (formData: MDAWorksheetSelectorFormState) => void;
    onCancel: () => void;
    executionGroupNumber?: string;
}

enum WizardStep {
    WORKSHEET_SELECTION = 0,
    DATA_SOURCE_SELECTION = 1,
    SOURCE_LINE_SELECTION = 2,
}

type WizardState = {
    currentStep: WizardStep;
    formData: Partial<MDAWorksheetSelectorFormState>;
};

const STEP_TO_FIELD_OWNERSHIP: Record<WizardStep, (keyof MDAWorksheetSelectorFormState)[]> = {
    [WizardStep.WORKSHEET_SELECTION]: [
        'worksheetId', 
        'worksheetVersion', 
        'worksheetName', 
    ],
    [WizardStep.DATA_SOURCE_SELECTION]: [
        'worksheetDatasourceId',
        'worksheetDatasourceS3PresignUrl',
        'worksheetExecutionId',
        'worksheetDatasourceDescription',
    ],
    [WizardStep.SOURCE_LINE_SELECTION]: ['selectedSourceLineIdentifiers'],
};

export function MDAWorksheetSelector(props: MDAWorksheetSelectorProps) {
    const { calcBuilderDispatch } = useContext(CalculationBuilderContext);
    const { state, services, dispatch } = useContext(DataSourcesContext);
    const {
        recordBeingEdited,
        dataSourceRecords,
        customCoaReference = new Map<string, string>(),
        recordBeingEditedOriginal,
    } = state;

    const [recordMode, setRecordMode] = useState<RecordMode>(RecordMode.NEW_RECORD);
    const [wizardState, setWizardState] = useState<WizardState>({
        currentStep: WizardStep.WORKSHEET_SELECTION,
        formData: props.initialSelectionData || {},
    });
    const [recordNeedsSaving, setRecordNeedsSaving] = useState(undefined as string | undefined);
    const [bulkRecordsToSave, setBulkRecordsToSave] = useState<DataSourceRecord[] | undefined>(undefined);
    const [triggerBulkSave, setTriggerBulkSave] = useState<string | undefined>(undefined);

    const { onComplete } = props;
    const [saveDataSourceRecordResponse, isSaving, saveError] = services.dataSourcesService.saveDataSource(
        customCoaReference,
        false,
        recordBeingEdited,
        recordNeedsSaving
    );

    const [bulkSaveResponses, isBulkSaving, bulkSaveError] = services.dataSourcesService.saveBulkDataSources(
        customCoaReference,
        bulkRecordsToSave,
        triggerBulkSave
    );

    useEffect(() => {
        if (!!recordBeingEdited?.multiAllocationWorksheetId) {
            setRecordMode(RecordMode.VIEW);
        } else {
            setRecordMode(RecordMode.NEW_RECORD);
        }
    }, []);

    const editFromSummary = () => {
        setRecordMode(RecordMode.UPDATE_EXISTING);
    };

    useEffect(() => {
        if (saveDataSourceRecordResponse == null) {
            return;
        }
        if (recordBeingEditedOriginal?.description != recordBeingEdited?.description) {
            calcBuilderDispatch(CALC_BUILDER_ACTIONS.SET_REFRESH_CALC_STEPS_FLAG.withPayload(true));
        }
        dispatch(
            ACTIONS.SAVE_RECORD.withPayload({
                recordID: recordBeingEdited?.datasourceId,
                updatedRecord: saveDataSourceRecordResponse,
            })
        );
        calcBuilderDispatch(CALC_BUILDER_ACTIONS.CHECK_AND_REFRESH_CALCULATION_INFORMATION);
        onComplete?.(wizardState.formData as MDAWorksheetSelectorFormState);
    }, [saveDataSourceRecordResponse]);

    useEffect(() => {
        if (StringUtils.isNullOrEmpty(saveError)) {
            return;
        }
        dispatch(ACTIONS.SET_SAVE_ERROR.withPayload(saveError));
        calcBuilderDispatch(CALC_BUILDER_ACTIONS.CHECK_AND_REFRESH_CALCULATION_INFORMATION);
    }, [saveError]);

    useEffect(() => {
        if (bulkSaveResponses.length === 0) {
            return;
        }

        // Trigger data sources refresh to reload all records from backend
        calcBuilderDispatch(CALC_BUILDER_ACTIONS.SET_REFRESH_DATASOURCES_FLAG.withPayload(true));
        calcBuilderDispatch(CALC_BUILDER_ACTIONS.SET_REFRESH_CALC_STEPS_FLAG.withPayload(true));
        calcBuilderDispatch(CALC_BUILDER_ACTIONS.CHECK_AND_REFRESH_CALCULATION_INFORMATION);
        onComplete?.(wizardState.formData as MDAWorksheetSelectorFormState);
    }, [bulkSaveResponses]);

    useEffect(() => {
        if (StringUtils.isNullOrEmpty(bulkSaveError)) {
            return;
        }
        dispatch(ACTIONS.SET_SAVE_ERROR.withPayload(bulkSaveError));
        calcBuilderDispatch(CALC_BUILDER_ACTIONS.CHECK_AND_REFRESH_CALCULATION_INFORMATION);
    }, [bulkSaveError]);

    const getSummary = (): KeyValuePair[] => {
        const summaryItems: KeyValuePair[] = [];

        if (recordBeingEdited?.multiAllocationWorksheetName) {
            summaryItems.push({ key: 'Worksheet', value: recordBeingEdited.multiAllocationWorksheetName });
        }

        if (recordBeingEdited?.multiAllocationWorksheetValueIdentifier?.function) {
            summaryItems.push({
                key: 'Function',
                value: recordBeingEdited.multiAllocationWorksheetValueIdentifier.function,
            });
        }

        if (recordBeingEdited?.multiAllocationWorksheetValueIdentifier?.companyCode) {
            summaryItems.push({
                key: 'Company Code',
                value: recordBeingEdited.multiAllocationWorksheetValueIdentifier.companyCode,
            });
        }

        return summaryItems;
    };

    const saveDataSource = (formData: MDAWorksheetSelectorFormState) => {
        if (!recordBeingEdited) {
            return;
        }

        const selectedSourceLines = formData.selectedSourceLineIdentifiers || [];

        if (selectedSourceLines.length === 0) {
            return;
        }

        // Handle multiple data source creation for NEW_RECORD mode
        if (recordMode === RecordMode.NEW_RECORD && selectedSourceLines.length > 1) {
            const allDescriptions = dataSourceRecords.map((x) => x.description);
            const recordsToCreate: DataSourceRecord[] = [];

            // Create a record for each selected source line
            for (const sourceLine of selectedSourceLines) {
                const newRecord: DataSourceRecord = {
                    ...recordBeingEdited,
                    isNewRecord: true,
                };

                populateFormData(newRecord, formData, sourceLine);

                // Validate description for this record
                const errorMap = services.dataSourcesService.validateDataSourceDescription(newRecord.description, [
                    ...allDescriptions,
                    ...recordsToCreate.map((r) => r.description),
                ]);

                if (errorMap.size > 0) {
                    dispatch(
                        ACTIONS.SET_ERRORS.withPayload({
                            recordID: recordBeingEdited?.datasourceId,
                            value: errorMap,
                        })
                    );
                    return;
                }

                recordsToCreate.push(newRecord);
            }

            // Clear errors and trigger bulk save
            dispatch(ACTIONS.CLEAR_ERRORS.withPayload({ recordID: recordBeingEdited?.datasourceId }));
            setBulkRecordsToSave(recordsToCreate);
            setTriggerBulkSave(new Date().toISOString());
        } else {
            // Single selection mode (for UPDATE_EXISTING or single selection in NEW_RECORD)
            const sourceLine = selectedSourceLines[0];
            populateFormData(recordBeingEdited, formData, sourceLine);

            const errorMap = services.dataSourcesService.validateDataSourceDescription(
                recordBeingEdited.description,
                dataSourceRecords.map((x) => x.description)
            );
            if (errorMap.size > 0) {
                dispatch(
                    ACTIONS.SET_ERRORS.withPayload({ recordID: recordBeingEdited?.datasourceId, value: errorMap })
                );
                return;
            }

            dispatch(ACTIONS.CLEAR_ERRORS.withPayload({ recordID: recordBeingEdited?.datasourceId }));
            setRecordNeedsSaving(new Date().toISOString());
        }
    };

    const handleNextOrSave = useCallback(
        (stepData: Partial<MDAWorksheetSelectorFormState>) => {
            setWizardState((state) => {
                const mergedFormData = { ...state.formData, ...stepData };
                // Clear fields from subsequent steps when current step data changes
                const cleanedFormData = clearDependentStepFields(state.currentStep, mergedFormData);

                // Only complete the wizard if we're on the last step
                if (state.currentStep === WizardStep.SOURCE_LINE_SELECTION) {
                    saveDataSource(cleanedFormData as MDAWorksheetSelectorFormState);
                }
                return {
                    ...state,
                    currentStep:
                        state.currentStep < WizardStep.SOURCE_LINE_SELECTION
                            ? state.currentStep + 1
                            : state.currentStep,
                    formData: cleanedFormData,
                };
            });
        },
        [setWizardState]
    );

    const handleBack = useCallback(() => {
        if (wizardState.currentStep > WizardStep.WORKSHEET_SELECTION) {
            setWizardState((prev) => ({
                ...prev,
                currentStep: prev.currentStep - 1,
            }));
        }
    }, [wizardState]);

    const spinnerComponent = (
                <Box padding={{ top: 'l', bottom: 'l', left: 'xxl', right: 'xxl' }}>
                    <SpaceBetween size="m" direction="horizontal">
                        <Spinner variant="disabled" />
                        <Box> Saving data sources... </Box>
                    </SpaceBetween>
                </Box>
            )

    const renderCurrentStep = () => {
        if (isSaving || isBulkSaving) {
            return spinnerComponent;
        }
        switch (wizardState.currentStep) {
            case WizardStep.WORKSHEET_SELECTION:
                return (
                    <WorksheetSelectionTable
                        initialData={wizardState.formData}
                        onCancel={props.onCancel}
                        onSave={handleNextOrSave}
                        recordMode={recordMode}
                        executionGroupNumber={props.executionGroupNumber}
                    />
                );

            case WizardStep.DATA_SOURCE_SELECTION:
                return (
                    <DataSourceSelectionTable
                        initialData={wizardState.formData}
                        onCancel={props.onCancel}
                        onBack={handleBack}
                        onSave={handleNextOrSave}
                        recordMode={recordMode}
                    />
                );

            case WizardStep.SOURCE_LINE_SELECTION:
                return (
                    <SourceLineSelectionTable
                        initialData={wizardState.formData}
                        onCancel={props.onCancel}
                        onBack={handleBack}
                        onSave={handleNextOrSave}
                        recordMode={recordMode}
                    />
                );

            default:
                return null;
        }
    };

    return recordMode === RecordMode.VIEW ? (
        <DataKeySummaryView
            header="Selected MDA worksheet configuration"
            summaryItems={getSummary()}
            showEdit={recordBeingEdited?.isEditing || false}
            onEdit={editFromSummary}
        />
    ) : (
        <Box>{renderCurrentStep()}</Box>
    );
}

function populateFormData(
    record: DataSourceRecord,
    formData: MDAWorksheetSelectorFormState,
    sourceLine: WorksheetValueIdentifier
) {
    // Populate MDA worksheet fields
    record.value = String(sourceLine.value);
    record.dataKey = formData.worksheetName;
    record.description = record.description || generateDataSourceDescription(formData, sourceLine);
    record.multiAllocationWorksheetId = formData.worksheetId;
    record.multiAllocationWorksheetVersion = formData.worksheetVersion;
    record.multiAllocationWorksheetName = formData.worksheetId;
    record.multiAllocationWorksheetDatasourceId = formData.worksheetDatasourceId;
    record.multiAllocationWorksheetValueIdentifier = {
        cellId: sourceLine.xcellId,
        companyCode: sourceLine.companyCode,
        function: sourceLine.function,
    };
}

function generateDataSourceDescription(
    { worksheetName, worksheetDatasourceDescription }: MDAWorksheetSelectorFormState,
    { companyCode, function: segmentFunction }: WorksheetValueIdentifier
) {
    return `${worksheetName}_${worksheetDatasourceDescription}_${companyCode}_${segmentFunction}`;
}

function clearDependentStepFields(
    currentStep: WizardStep,
    formData: Partial<MDAWorksheetSelectorFormState>
): Partial<MDAWorksheetSelectorFormState> {
    const subsequentSteps = Object.keys(STEP_TO_FIELD_OWNERSHIP)
        .map(Number)
        .filter((step) => step > currentStep) as WizardStep[];

    const fieldsToRemove = subsequentSteps.flatMap((step) => STEP_TO_FIELD_OWNERSHIP[step]);

    fieldsToRemove.forEach((field) => {
        delete formData[field];
    });

    return formData;
}
